import React, { useEffect, useState } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';

const DetailsPage: React.FC = () => {
  const [seasonDetails, setSeasonDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();

  useEffect(() => {
    const fetchSeasonDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get(`${API_URLS.GET_SEASON_BY_ID}/${seasonId}`, {
          params: { clientId },
        });

        if (response.data.success) {
          setSeasonDetails(response.data.data);
        } else {
          setError(response.data.message || 'Failed to fetch season details');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch season details');
      } finally {
        setLoading(false);
      }
    };

    if (clientId && seasonId) {
      fetchSeasonDetails();
    }
  }, [clientId, seasonId]);

  if (loading) {
    return (
      <div className="w-full mb-6 overflow-hidden">
        <div className="flex items-center justify-center py-8">
          <div className="text-lg">Loading season details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full mb-6 overflow-hidden">
        <div className="flex items-center justify-center py-8">
          <div className="text-red-600 text-lg">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8 max-w-7xl">
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-sd-navy">Season Details</h1>
        <p className="mt-2 text-sm sm:text-base text-sd-navy font-medium max-w-3xl">
          Complete season information and settings. These fields are read-only here; modify them from the admin setup if
          changes are required.
        </p>
      </header>
      <div className="flex flex-col pb-4 gap-6">
        {/* Season Name */}
        <div className="flex flex-row gap-3 text-base font-medium">
          <p className="min-w-24 pt-1 pl-2">Season Name</p>
          <input
            type="text"
            value={seasonDetails?.name || ''}
            disabled
            className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none"
          />
        </div>

        {/* Description field removed per request */}

        {/* Start Date */}
        <div className="flex flex-row gap-3 text-base font-medium">
          <p className="min-w-24 pt-1 pl-2">Start Date</p>
          <input
            type="date"
            value={seasonDetails?.startDate ? new Date(seasonDetails.startDate).toISOString().split('T')[0] : ''}
            disabled
            className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none"
          />
        </div>

        {/* End Date */}
        <div className="flex flex-row gap-3 text-base font-medium">
          <p className="min-w-24 pt-1 pl-2">End Date</p>
          <input
            type="date"
            value={seasonDetails?.endDate ? new Date(seasonDetails.endDate).toISOString().split('T')[0] : ''}
            disabled
            className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none"
          />
        </div>

        {/* Shot Clock */}
        <div className="flex flex-row gap-3 text-base font-medium">
          <p className="min-w-24 pt-1 pl-2">Shot Clock</p>
          <select
            value={seasonDetails?.shotClock ? 'Yes' : 'No'}
            disabled
            className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none"
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        {/* Period Type */}
        <div className="flex flex-row gap-3 text-base font-medium">
          <p className="min-w-24 pt-1 pl-2">Period Type</p>
          <select
            value={seasonDetails?.halfPeriod ? 'Halves' : 'Quarters'}
            disabled
            className="w-full px-2 py-1 rounded border border-[#22c55e] bg-sd-light focus:outline-none"
          >
            <option value="Quarters">Quarters</option>
            <option value="Halves">Halves</option>
          </select>
        </div>

        {/* Statistics */}
        <div className="bg-white border border-sd-line rounded-lg mt-2">
          <div className="bg-sd-navy text-white text-lg font-semibold px-4 py-2 rounded-t-lg">Season Statistics</div>
          <div className="px-4 py-3 space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Total Games:</span>
              <span className="text-gray-900 font-semibold">{seasonDetails?.totalGames || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Wins:</span>
              <span className="text-green-600 font-semibold">{seasonDetails?.wins || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Losses:</span>
              <span className="text-red-600 font-semibold">{seasonDetails?.losses || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700 font-medium">Win Percentage:</span>
              <span className="text-blue-600 font-semibold">
                {seasonDetails?.totalGames && seasonDetails.totalGames > 0
                  ? Math.round(((seasonDetails.wins || 0) / seasonDetails.totalGames) * 100)
                  : 0}
                %
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsPage;
